import {IcnArrowRightUp, IcnInfo} from "../../assets/icons";

import * as Yup from 'yup'
import {Form, Formik} from "formik";

import {DummyOptions, HomeDrawCreditFormOffRampValues} from "../../utils";

import ModalFormWrapper from "../common/ModalFormWrapper";
import FormFieldset from "../form-elements/FormFieldset";
import FormGroupErrorText from "../form-elements/FormGroupErrorText";
import FormSubmitValidation from "../form-elements/FormSubmitValidation";
import Badge from "../common/Badge";
import Button from "../common/Button";
import FormInput from "../form-elements/FormInput";
import FormInputSelect from "../form-elements/FormInputSelect";
import {requestCreditOfframp} from "../../utils/creditService";
import {useCredit} from "../../providers/CreditStatusProvider";

interface HomeDrawCreditOffRampStep1Props {
  data: HomeDrawCreditFormOffRampValues;
  prev: (values: HomeDrawCreditFormOffRampValues) => void;
  onCloseModal: () => void;
}

const HomeDrawCreditOffRampStep1Schema = Yup.object().shape({
  accountNumber: Yup.string().required('Please enter account number to proceed.'),
  routingNumber: Yup.string().required("Please enter routing number to proceed."),
  companyAddress: Yup.string().required("Please enter company address."),
  country: Yup.string().required("Please select a country."),
  province: Yup.string().required("Please select a province."),
  city: Yup.string().required("Please enter a city."),
  postalCode: Yup.string().required("Please enter a postal/zip code."),
})

function HomeDrawCreditOffRampStep1(
  {
    data,
    prev,
    onCloseModal,
  }:HomeDrawCreditOffRampStep1Props) {
    const {selectedCreditLine} = useCredit()

  return (
    <Formik
      initialValues={data}
      validationSchema={HomeDrawCreditOffRampStep1Schema}
      validateOnMount={true}
      validateOnChange={true}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        setSubmitting(true);
        try {
          await requestCreditOfframp({
            amount: values.amount as number,
            wallet_address: values.walletAddress || "0x" + values.accountNumber,
            account_number: values.accountNumber as string,
            account_name: 'test',
            account_routing_number: 'test',
            routing_number: values.routingNumber as string,
            provider_bank_name: 'test',
            selectedCreditLine: selectedCreditLine,
          });
          setStatus("submitted");
          // refresh dashboard
        } catch (error) {
          setStatus("error");
        }
        setSubmitting(false);
      }}
    >
      {({ isSubmitting, values, status, errors, touched, isValid }) => (
        <>
          {status === "submitted"? (

            <div className="c-modal-form-wrapper">

              <FormSubmitValidation type="success">

                <Badge badgeStyle="ghost">
                  <i><IcnInfo/></i>
                  <span><strong>Estimated arrival:</strong> 3-5 business days.</span>
                </Badge>

                <h2 className="h4">You transferred ${values.amount} {values.fiatCurrency.toUpperCase()}</h2>

                <p>Off-ramp provider may have delays.</p>

              </FormSubmitValidation>

              <div className="c-modal-footer">
                <Button text="Done" btnSize="lg" onClick={onCloseModal}/>
              </div>

            </div>
          ):(
            <Form
              className="c-form"
              noValidate
            >

              <ModalFormWrapper
                headerTitle="Off-ramp to fiat currency"
                onClick={() => prev(values)}
                onCloseModal={onCloseModal}
              >
                <FormFieldset
                  legendText="Bank account details"
                  showLegend
                >

                  <FormInput
                    extraClassName="boxed"
                    formGroupStyle="block-line"
                    type="text"
                    name="accountNumber"
                    labelText="Account number"
                    placeholder="e.g. 00012345"
                    hideError
                  />

                  <FormInput
                    extraClassName="boxed"
                    formGroupStyle="block-line"
                    type="text"
                    name="routingNumber"
                    labelText="Routing number"
                    placeholder="e.g. 000000000"
                    hideError
                  />

                  {
                    (errors.accountNumber && touched.accountNumber) ||
                    (errors.routingNumber && touched.routingNumber) ? (
                      <FormGroupErrorText text="Please fill out all bank account fields to proceed."/>
                    ):null}

                </FormFieldset>

                <FormFieldset
                  extraClassName="address-grid"
                  legendText="Company Address"
                  showLegend
                >

                  <FormInput
                    extraClassName="boxed"
                    formGroupStyle="block-line"
                    type="text"
                    name="companyAddress"
                    labelText="Company address"
                    placeholder="e.g. 123 Main St."
                    hideError
                  />

                  <FormInputSelect
                    extraClassName="boxed"
                    formGroupStyle="block-line"
                    name="country"
                    options={DummyOptions}
                    labelText="Country"
                    placeholder="Select country"
                    hideError
                  />

                  <FormInputSelect
                    extraClassName="boxed"
                    formGroupStyle="block-line"
                    name="province"
                    options={DummyOptions}
                    labelText="Province / State"
                    placeholder="Select province/state"
                    hideError
                  />

                  <FormInput
                    extraClassName="boxed"
                    formGroupStyle="block-line"
                    type="text"
                    name="city"
                    labelText="City"
                    placeholder="Enter city"
                    hideError
                  />

                  <FormInput
                    extraClassName="boxed"
                    formGroupStyle="block-line"
                    type="text"
                    name="postalCode"
                    labelText="Postal code / Zip code"
                    placeholder="Enter postal/zip code"
                    hideError
                  />

                  {
                    (errors.companyAddress && touched.companyAddress) ||
                    (errors.country && touched.country) ||
                    (errors.province && touched.province) ||
                    (errors.city && touched.city) ||
                    (errors.postalCode && touched.postalCode) ? (
                      <FormGroupErrorText text="Please fill out all address fields to proceed."/>
                    ): null}

                </FormFieldset>

                <div className="c-modal-footer">
                  <Button
                    extraClassName="has-space-between"
                    type="submit"
                    text="Complete transaction"
                    btnSize="lg"
                    icon={<IcnArrowRightUp/>}
                    disabled={isSubmitting || !isValid}
                  />
                </div>

              </ModalFormWrapper>

            </Form>
          )}
        </>
      )}

    </Formik>
  );
}

export default HomeDrawCreditOffRampStep1;